import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const GOALS = [
  { id: "longevity", label: "Longevity" },
  { id: "vitality", label: "Vitality & Energy" },
  { id: "sleep", label: "Sleep" },
  { id: "gut", label: "Gut & Digestion" },
  { id: "hormones", label: "Hormones" },
  { id: "immunity", label: "Immunity" },
];

export default function Intake() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan");
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("");
  const [goals, setGoals] = useState([]);
  const [conditions, setConditions] = useState("");
  const [ancestry, setAncestry] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleGoal = (id) =>
    setGoals((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (goals.length === 0) { toast.error("Please choose at least one focus."); return; }
    setBusy(true);
    try {
      await api.post("/intake", {
        age: parseInt(age, 10),
        sex,
        goals,
        conditions: conditions ? conditions.split(",").map((s) => s.trim()) : [],
        ancestry,
        notes,
      });
      await refresh();
      toast.success("Your protocols have been woven.");
      nav(plan ? `/pricing?plan=${plan}` : "/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save intake");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 py-20 max-w-2xl mx-auto">
        <p className="label-eyebrow mb-3">Intake ceremony</p>
        <h1 className="font-serif text-4xl text-[#2D322B] mb-4">Tell us where you stand on the path.</h1>
        <p className="text-[#5C6359] mb-10">Your answers shape the protocols we draw from the catalog. Honest, brief — no medical history needed.</p>

        <form onSubmit={submit} className="space-y-8" data-testid="intake-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="label-eyebrow block mb-2">Age</label>
              <input type="number" required value={age} onChange={(e) => setAge(e.target.value)} data-testid="intake-age" className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Sex / Body</label>
              <select required value={sex} onChange={(e) => setSex(e.target.value)} data-testid="intake-sex" className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]">
                <option value="">Choose…</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="intersex">Intersex / other</option>
                <option value="prefer_not">Prefer not to say</option>
              </select>
            </div>
          </div>

          <div>
            <p className="label-eyebrow mb-3">Focus areas (choose any)</p>
            <div className="grid grid-cols-2 gap-3">
              {GOALS.map((g) => {
                const on = goals.includes(g.id);
                return (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => toggleGoal(g.id)}
                    data-testid={`goal-${g.id}`}
                    className={`text-left rounded-2xl border px-5 py-4 transition-colors ${on ? "bg-[#2D322B] text-[#F9F6F0] border-[#2D322B]" : "bg-[#F1ECE4] border-[#D1C7B8] text-[#2D322B] hover:border-[#C05C42]"}`}
                  >
                    <span className="font-serif text-lg">{g.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Known conditions (comma-separated)</label>
            <input value={conditions} onChange={(e) => setConditions(e.target.value)} data-testid="intake-conditions" placeholder="e.g. hashimoto's, insulin resistance" className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Ancestry / lineage you connect with (optional)</label>
            <input value={ancestry} onChange={(e) => setAncestry(e.target.value)} data-testid="intake-ancestry" className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Anything else?</label>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="intake-notes" className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B]" />
          </div>

          <button type="submit" disabled={busy} className="btn-primary" data-testid="intake-submit">
            {busy ? "Weaving your protocols…" : "Reveal my protocols"}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}
